import React, { useState, useEffect, useRef, createRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import InfoCard from '../InfoCard';
import { PlusIcon, TrashIcon, ForSaleIcon, UnderConstructionIcon, PlannedIcon, AlertTriangleIcon, CopyIcon } from '../Icon';
import Button from '../Button/Button';
import styles from './EditingPanel.module.css';


// Системные свойства, которые не нужно редактировать
const SYSTEM_PROPERTIES = ['id', 'type', 'geometry', 'intersecting_plots', 'objects_on_plot'];

// Вспомогательная функция для парсинга JSON-строк в массивы
function parseArrayProperty(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
}

// Fallback свойства на случай, если метаданные не загружены
const FALLBACK_PROPERTIES_BY_TYPE = {
    plot: [
        { name: 'name', dataType: 'text', inputType: 'text', label: 'Название' },
        { name: 'cadastral_number', dataType: 'text', inputType: 'text', label: 'Кадастровый номер' },
        { name: 'category', dataType: 'select', inputType: 'select', label: 'Категория' },
        { name: 'owner', dataType: 'text', inputType: 'text', label: 'Владелец' },
        { name: 'tenants', dataType: 'array', label: 'Арендаторы' },
        { name: 'for_sale', dataType: 'boolean', inputType: 'checkbox', label: 'В продаже' },
        { name: 'is_planned', dataType: 'boolean', inputType: 'checkbox', label: 'Запланировано' },
        { name: 'photos', dataType: 'array', label: 'Фотографии' },
        { name: 'links', dataType: 'array', label: 'Ссылки' },
        { name: 'description', dataType: 'text', inputType: 'textarea', label: 'Дополнительная информация' },
        { name: 'custom_fields', dataType: 'array', label: 'Дополнительные характеристики' },
        { name: 'area', dataType: 'number', inputType: 'number', label: 'Площадь (м²)' },
    ],
    building: [
        { name: 'name', dataType: 'text', inputType: 'text', label: 'Название' },
        { name: 'cadastral_number', dataType: 'text', inputType: 'text', label: 'Кадастровый номер' },
        { name: 'category', dataType: 'select', inputType: 'select', label: 'Категория' },
        { name: 'height', dataType: 'number', inputType: 'number', label: 'Высота (м)' },
        { name: 'owner', dataType: 'text', inputType: 'text', label: 'Владелец' },
        { name: 'tenants', dataType: 'array', label: 'Арендаторы' },
        { name: 'for_sale', dataType: 'boolean', inputType: 'checkbox', label: 'В продаже' },
        { name: 'is_building', dataType: 'boolean', inputType: 'checkbox', label: 'В строительстве' },
        { name: 'is_planned', dataType: 'boolean', inputType: 'checkbox', label: 'Запланировано' },
        { name: 'photos', dataType: 'array', label: 'Фотографии' },
        { name: 'links', dataType: 'array', label: 'Ссылки' },
        { name: 'description', dataType: 'text', inputType: 'textarea', label: 'Дополнительная информация' },
        { name: 'custom_fields', dataType: 'array', label: 'Дополнительные характеристики' },
        { name: 'area', dataType: 'number', inputType: 'number', label: 'Площадь (м²)' },
    ],
    line: [
        { name: 'name', dataType: 'text', inputType: 'text', label: 'Название' },
        { name: 'category', dataType: 'select', inputType: 'select', label: 'Категория' },
        { name: 'owner', dataType: 'text', inputType: 'text', label: 'Владелец' },
        { name: 'for_sale', dataType: 'boolean', inputType: 'checkbox', label: 'В продаже' },
        { name: 'is_building', dataType: 'boolean', inputType: 'checkbox', label: 'В строительстве' },
        { name: 'is_planned', dataType: 'boolean', inputType: 'checkbox', label: 'Запланировано' },
        { name: 'photos', dataType: 'array', label: 'Фотографии' },
        { name: 'links', dataType: 'array', label: 'Ссылки' },
        { name: 'description', dataType: 'text', inputType: 'textarea', label: 'Дополнительная информация' },
        { name: 'custom_fields', dataType: 'array', label: 'Дополнительные характеристики' }
    ],
    marker: [
        { name: 'name', dataType: 'text', inputType: 'text', label: 'Название' },
        { name: 'category', dataType: 'select', inputType: 'select', label: 'Категория' },
        { name: 'photos', dataType: 'array', label: 'Фотографии' },
        { name: 'links', dataType: 'array', label: 'Ссылки' },
        { name: 'description', dataType: 'text', inputType: 'textarea', label: 'Дополнительная информация' },
        { name: 'custom_fields', dataType: 'array', label: 'Дополнительные характеристики' },
    ]
};

// Функция для получения читаемого названия свойства (fallback)
function getPropertyLabel(propName) {
    const labels = {
        name: 'Название',
        cadastral_number: 'Кадастровый номер',
        owner: 'Владелец',        
        category: 'Категория',
        color: 'Цвет',
        height: 'Высота (м)',
        for_sale: 'В продаже',
        is_building: 'В строительстве',
        is_planned: 'Запланировано',
        width: 'Ширина (м)',
        length: 'Длина (м)',
        area: 'Площадь (м²)',
        address: 'Адрес',        
        description: 'Описание'
    };
    return labels[propName] || propName;
}

const NewMarkerModal = ({ isVisible, onClose, onSave }) => {
    const Button = require('../Button/Button').default;
    const [name, setName] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (!imageFile) {
            setPreview(undefined);
            return;
        }
        const objectUrl = URL.createObjectURL(imageFile);
        setPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    const handleSave = () => {
        if (!name || !imageFile) {
            toast.warn('Введите название и выберите изображение');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append('image', imageFile);

        fetch('/api/upload/image', { method: 'POST', body: formData })
            .then(res => res.json())
            .then(data => {
                const newCategory = {
                    id: `marker_${Date.now()}`,
                    name: name,
                    image_url: data.url,
                    image_filename: data.filename,
                };
                onSave(newCategory);
                // Reset state
                setName('');
                setImageFile(null);
                setPreview(null);
            })
            .catch(() => toast.error('Ошибка загрузки изображения'))
            .finally(() => {
                setIsUploading(false);
                // Сбрасываем состояние даже в случае ошибки
                onClose();
            });
    };

    if (!isVisible) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h3>Новая категория метки</h3>
                <input
                    type="text"
                    placeholder="Название категории"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={styles.editInput}
                />
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0])}
                    className={styles.editInput}
                />
                {preview && <img src={preview} alt="Предпросмотр" className={styles.markerPreview} />}
                <div className={styles.modalActions}>
                    <Button variant="default" onClick={onClose}>Отмена</Button>
                    <Button variant="primary" onClick={handleSave} disabled={isUploading}>
                        {isUploading ? 'Загрузка...' : 'Сохранить'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const EditingPanel = ({ feature, onSave, onCancel, onDuplicate, availableProperties, legendData, onLegendUpdate, mapData, duplicateInfo }) => {
    const [properties, setProperties] = useState({});
    const [isVisible, setIsVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('main');
    const [isMarkerModalVisible, setMarkerModalVisible] = useState(false);
    const tabsContainerRef = useRef(null);
    const tabRefs = useRef([]);

    const featureType = feature?.properties?.type;

    const tabs = useMemo(() => {
        const hasMainDup = duplicateInfo && (duplicateInfo.hasGeometryDuplicate || duplicateInfo.hasCadastralDuplicate || duplicateInfo.hasProximityDuplicate);
        const dupCount = duplicateInfo?.otherFeatures?.length || 1;
        
        let typeText = 'Дубль';
        if (duplicateInfo?.hasGeometryDuplicate) typeText = 'Дубль геометрии';
        else if (duplicateInfo?.hasCadastralDuplicate) typeText = 'Дубль кад. номера';
        else if (duplicateInfo?.hasProximityDuplicate) typeText = 'Дубль координат';

        const mainDupText = hasMainDup ? `${typeText} (${dupCount})` : '';

        const list = [{ 
            id: 'main', 
            label: 'Основное',
            hasDuplicate: !!hasMainDup,
            duplicateText: mainDupText
        }];
        if (featureType !== 'marker') {
            list.push({ id: 'ownership', label: 'Владение' });
            list.push({ id: 'status', label: 'Статусы' });
        }
        list.push({ id: 'media', label: 'Медиа' });
        list.push({ id: 'extra', label: 'Доп. инфо' });
        return list;
    }, [featureType, duplicateInfo]);


    useEffect(() => {
        if (feature) {
            // Устанавливаем высоту по умолчанию для новых зданий
            if (feature.properties.type === 'building' && !feature.properties.hasOwnProperty('height')) {
                feature.properties.height = 2;
            }
            // Инициализируем массивы, гарантируя что они являются массивами
            // Если данные приходят как JSON-строки из БД, парсим их
            const props = feature.properties || {};
            props.tenants = parseArrayProperty(props.tenants);
            props.links = parseArrayProperty(props.links);
            props.photos = parseArrayProperty(props.photos);
            props.custom_fields = parseArrayProperty(props.custom_fields);

            // Если есть категория, но нет цвета, и legendData загружен, устанавливаем цвет из легенды
            if (props.category && legendData) {
                let categories = [];
                const type = feature.properties.type;
                if (type === 'plot' || type === 'plot') categories = legendData.plots || [];
                else if (type === 'building') categories = legendData.buildings || [];
                else if (type === 'line') categories = legendData.roads || [];
                else if (type === 'marker') categories = legendData.markers || [];
                
                if (categories.length > 0) {
                    const selectedCategory = categories.find(cat => cat.id === props.category);
                    if (selectedCategory && selectedCategory.color && !props.color) {
                        props.color = selectedCategory.color;
                    }
                }
            }
            setProperties(props);
            setIsVisible(true);
            setActiveTab('main'); // Сбрасываем на первую вкладку при открытии
        } else {
            setIsVisible(false);
        }
    }, [feature, legendData]);

    // Эффект для прокрутки активной вкладки в видимую область
    useEffect(() => {
        const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);
        const activeTabRef = tabRefs.current[activeTabIndex];
        if (activeTabRef && activeTabRef.current) {
            activeTabRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [activeTab]);

    // Инициализируем массив refs один раз
    if (tabRefs.current.length !== tabs.length) {
        tabRefs.current = Array(tabs.length).fill().map((_, i) => tabRefs.current[i] || createRef());
    }

    const handlePropertyChange = (prop, value) => {
        setProperties(prev => ({ ...prev, [prop]: value }));
    };

    const handleArrayChange = (arrayName, index, field, value) => {
        const newArray = [...(properties[arrayName] || [])];
        newArray[index] = { ...newArray[index], [field]: value };
        handlePropertyChange(arrayName, newArray);
    };

    const addToArray = (arrayName, newItem) => {
        const newArray = [...(properties[arrayName] || []), newItem];
        handlePropertyChange(arrayName, newArray);
    };

    const removeFromArray = (arrayName, index) => {
        const newArray = [...(properties[arrayName] || [])];
        newArray.splice(index, 1);
        handlePropertyChange(arrayName, newArray);
    };

    const handleNewMarkerCategory = (newCategory) => {
        // Предполагается, что newCategory имеет вид { id, name, image_url, image_filename }
        // 1. Обновляем legendData локально
        const newLegendData = {
            ...legendData,
            markers: [...(legendData.markers || []), newCategory]
        };
        // 2. Отправляем на сервер для сохранения
        fetch('/api/legend', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newLegendData)
        })
        .then(res => {
            if (!res.ok) throw new Error('Server error');
            return res.json();
        })
        .then(() => {
            toast.success(`Категория метки "${newCategory.name}" создана`);
            if (onLegendUpdate) {
                onLegendUpdate();
            }
            // 4. Устанавливаем новую категорию для текущего объекта
            handlePropertyChange('category', newCategory.id);
            handlePropertyChange('icon_filename', newCategory.image_filename);
        })
        .catch(() => toast.error('Ошибка при создании новой категории'));

        setMarkerModalVisible(false);
    };

    const handleSave = () => {
        onSave({ ...feature, properties });
        // Если категория не выбрана, устанавливаем цвет по умолчанию
        if (!properties.category) {
            handlePropertyChange('color', '#808080'); // Серый цвет
        }
    };

    if (!feature) return null;

    const type = feature.properties.type;

    // Для новых объектов используем FALLBACK_PROPERTIES_BY_TYPE как основной источник полей.
    // Это делает UI предсказуемым и не зависимым от ответа сервера или наличия данных в БД.
    // availableProperties будет использоваться для добавления кастомных полей при редактировании существующих объектов.
    const propsConfig = FALLBACK_PROPERTIES_BY_TYPE[type] || [];

    // Получаем список категорий для данного типа
    const getCategories = () => {
        switch (type) {
            case 'plot': return legendData.plots || [];
            case 'building': return legendData.buildings || [];
            case 'line':
                return legendData.roads || [];
            case 'marker': return legendData.markers || [];
            default: return [];
        }
    };
    const categories = getCategories();

    const statusConfig = propsConfig.filter(p => p.inputType === 'checkbox').map(p => {
        let Icon;
        switch (p.name) {
            case 'for_sale': Icon = ForSaleIcon; break;
            case 'is_building': Icon = UnderConstructionIcon; break;
            case 'is_planned': Icon = PlannedIcon; break;
            default: Icon = null;
        }
        return { ...p, Icon };
    });

    const renderStatusProperty = (prop) => {
        if (!prop.Icon) {
            return renderProperty(prop); // Fallback for statuses without icons
        }
        return (
            <label key={prop.name} className={`${styles.editLabel} ${styles.statusLabel}`}>
                <prop.Icon size={20} className={styles.statusIcon} />
                <span>{prop.label}</span>
                <input
                    className={styles.statusCheckbox}
                    type="checkbox"
                    checked={!!properties[prop.name]}
                    onChange={(e) => handlePropertyChange(prop.name, e.target.checked)}
                />
            </label>
        );
    };

    const renderProperty = (prop) => {
        if (SYSTEM_PROPERTIES.includes(prop.name) || prop.dataType === 'array') return null;

        const value = properties[prop.name];
        const label = prop.label || getPropertyLabel(prop.name);

        switch (prop.inputType) {
            case 'checkbox':
                return (
                    <label key={prop.name} className={`${styles.editLabel} ${styles.checkboxLabel}`}>
                        <input
                            type="checkbox"
                            checked={!!value}
                            onChange={(e) => handlePropertyChange(prop.name, e.target.checked)}
                        />
                        <span>{label}</span>
                    </label>
                );
            case 'number':
                return (
                    <label key={prop.name} className={styles.editLabel}>
                        <span>{label}:</span>
                        <input
                            type="number"
                            className={styles.editInput}
                            value={value || ''}
                            onChange={(e) => handlePropertyChange(prop.name, parseFloat(e.target.value) || 0)}
                            readOnly={prop.readOnly}
                        />
                    </label>
                );
            case 'text':
                return (
                    <label key={prop.name} className={styles.editLabel}>
                        <span>{label}:</span>
                        <input
                            type="text"
                            className={styles.editInput}
                            value={value || ''}
                            onChange={(e) => handlePropertyChange(prop.name, e.target.value)}
                            readOnly={prop.readOnly}
                        />
                        {prop.name === 'cadastral_number' && duplicateInfo?.hasCadastralDuplicate && (
                            <span className={styles.fieldDuplicateBadge}>
                                ⚠️ Дубль кад. номера ({duplicateInfo.otherFeatures?.length || 1})
                            </span>
                        )}
                    </label>
                );
            case 'select':
                if (prop.name === 'category') {
                    if (type === 'marker') {
                        return (
                            <div key={prop.name} className={styles.markerCategorySelector}>
                                <label className={styles.editLabel}>
                                    <span>{label}:</span>
                                    <select
                                        className={styles.editInput}
                                        value={value || ''}
                                        onChange={(e) => {
                                            const newCategoryId = e.target.value;
                                            handlePropertyChange(prop.name, newCategoryId);
                                            const selectedCategory = categories.find(cat => cat.id === newCategoryId);
                                            if (selectedCategory) {
                                                handlePropertyChange('icon_filename', selectedCategory.image_filename);
                                            }
                                        }}
                                    >
                                        <option value="">Выберите метку</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <Button variant="secondary" onClick={() => setMarkerModalVisible(true)} className={styles.addMarkerButton}>
                                    <PlusIcon size={16} />
                                </Button>
                            </div>
                        );
                    } else { // General category select for other types
                        return (
                            <label key={prop.name} className={styles.editLabel}>
                                <span>{label}:</span>
                                <select
                                    className={styles.editInput}
                                    value={value || ''}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handlePropertyChange(prop.name, newValue);
                                        if (newValue) {
                                            const selectedCategory = categories.find(cat => cat.id === newValue);
                                            if (selectedCategory) {
                                                handlePropertyChange('color', selectedCategory.color || '#cccccc');
                                                if (type === 'plot' || type === 'building') {
                                                    handlePropertyChange('hatch', selectedCategory.hatch || 'none');
                                                }
                                                if (type === 'line' || type === 'road') {
                                                    handlePropertyChange('lineType', selectedCategory.lineType || 'solid');
                                                }
                                            }
                                        } else {
                                            handlePropertyChange('color', '#808080'); // Сбрасываем цвет, если категория убрана
                                        }
                                    }}
                                >
                                    <option value="">Выберите категорию</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </label>
                        );
                    }
                } else { // Other select types (e.g., hatch, lineType if they were select inputs)
                    return (
                        <label key={prop.name} className={styles.editLabel}>
                            <span>{label}:</span>
                            <select
                                className={styles.editInput}
                                value={value || ''}
                                onChange={(e) => handlePropertyChange(prop.name, e.target.value)}
                            >
                                {prop.options?.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </label>
                    );
                }
            case 'textarea':
                return (
                    <textarea
                        key={prop.name}
                        className={styles.editInput}
                        value={value || ''}
                        placeholder={label}
                        onChange={(e) => handlePropertyChange(prop.name, e.target.value)}
                    />
                );
            default: // Fallback for any unhandled inputType, or if inputType is missing
                return (
                    <label key={prop.name} className={styles.editLabel}>
                        <span>{label}:</span>
                        <input
                            type="text"
                            className={styles.editInput}
                            value={value || ''}
                            onChange={(e) => handlePropertyChange(prop.name, e.target.value)}
                            readOnly={prop.readOnly}
                        />
                    </label>
                );
        }
    };

    const renderOwnershipSection = () => (
        <>
            <h4 className={styles.sectionTitle}>Владение и аренда</h4>
            {propsConfig.filter(p => p.name === 'owner').map(renderProperty)}
            <div className={styles.arraySection}>
                {(Array.isArray(properties.tenants) ? properties.tenants : []).map((tenant, index) => (
                    <div key={index} className={styles.arrayItem}>
                        <input type="text" placeholder="Название арендатора" value={tenant.name || ''} onChange={(e) => handleArrayChange('tenants', index, 'name', e.target.value)} className={styles.editInput} />
                        <input type="number" placeholder="Площадь (м²)" value={tenant.area || ''} onChange={(e) => handleArrayChange('tenants', index, 'area', parseFloat(e.target.value) || 0)} className={styles.editInput} />
                        <button onClick={() => removeFromArray('tenants', index)} className={styles.removeButton}><TrashIcon size={16} /></button>
                    </div>
                ))}
                <button onClick={() => addToArray('tenants', { name: '', area: 0 })} className={styles.addButton}><PlusIcon size={16} /> Добавить арендатора</button>
            </div>
        </>
    );

    const renderPhotosSection = () => (
        <>
            <h4 className={styles.sectionTitle}>Фотографии</h4>
            <div className={styles.arraySection}>
                {(properties.photos || []).map((photo, index) => (
                    <div key={index} className={styles.arrayItem}>
                        <input type="text" placeholder="URL изображения" value={photo.url || ''} onChange={(e) => handleArrayChange('photos', index, 'url', e.target.value)} className={styles.editInput} />
                        <input type="text" placeholder="Подпись" value={photo.caption || ''} onChange={(e) => handleArrayChange('photos', index, 'caption', e.target.value)} className={styles.editInput} />
                        <button onClick={() => removeFromArray('photos', index)} className={styles.removeButton}><TrashIcon size={16} /></button>
                    </div>
                ))}
                <button onClick={() => addToArray('photos', { url: '', caption: '' })} className={styles.addButton}><PlusIcon size={16} /> Добавить фото</button>
            </div>
        </>
    );

    const renderLinksSection = () => (
        <>
            <h4 className={styles.sectionTitle}>Ссылки</h4>
            <div className={styles.arraySection}>
                {(properties.links || []).map((link, index) => (
                    <div key={index} className={styles.arrayItem}>
                        <input type="text" placeholder="Текст ссылки" value={link.text || ''} onChange={(e) => handleArrayChange('links', index, 'text', e.target.value)} className={styles.editInput} />
                        <input type="text" placeholder="URL" value={link.url || ''} onChange={(e) => handleArrayChange('links', index, 'url', e.target.value)} className={styles.editInput} />
                        <button onClick={() => removeFromArray('links', index)} className={styles.removeButton}><TrashIcon size={16} /></button>
                    </div>
                ))}
                <button onClick={() => addToArray('links', { text: '', url: '' })} className={styles.addButton}><PlusIcon size={16} /> Добавить ссылку</button>
            </div>
        </>
    );

    const renderExtraSection = () => (
        <>
            <h4 className={styles.sectionTitle}>Дополнительная информация</h4>
            {propsConfig.filter(p => p.name === 'description').map(renderProperty)}
            <h4 className={styles.sectionTitle} style={{ marginTop: '16px' }}>Дополнительные характеристики</h4>
            <div className={styles.arraySection}>
                {(properties.custom_fields || []).map((field, index) => (
                    <div key={index} className={styles.arrayItem}>
                        <input type="text" placeholder="Название" value={field.key || ''} onChange={(e) => handleArrayChange('custom_fields', index, 'key', e.target.value)} className={styles.editInput} />
                        <textarea placeholder="Значение" value={field.value || ''} onChange={(e) => handleArrayChange('custom_fields', index, 'value', e.target.value)} className={`${styles.editInput} ${styles.textareaInArray}`} />
                        <button onClick={() => removeFromArray('custom_fields', index)} className={styles.removeButton}><TrashIcon size={16} /></button>
                    </div>
                ))}
                <button onClick={() => addToArray('custom_fields', { key: '', value: '' })} className={styles.addButton}><PlusIcon size={16} /> Добавить характеристику</button>
            </div>
        </>
    );

    const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);

    return (
        <div className={`${styles.editingPanel} ${isVisible ? styles.visible : ''}`}>
            <InfoCard
                title="Настройка объекта"
                variant="outlined"
                className={styles.mainInfoCard}
                actions={[
                    { label: 'Отмена', onClick: onCancel, variant: 'default' },
                    { label: 'Сохранить', onClick: handleSave, variant: 'primary' }
                ]}
            >
                <div className={styles.editForm}>
                    <div className={styles.tabs} ref={tabsContainerRef}>
                        {tabs.map((tab, index) => (
                            <button
                                key={tab.id}
                                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''} ${tab.hasDuplicate ? styles.tabWithDuplicate : ''}`}
                                ref={tabRefs.current[index]}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <span>{tab.label}</span>
                                {tab.hasDuplicate && tab.duplicateText && (
                                    <span className={styles.tabDuplicateBadge}>{tab.duplicateText}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabContentContainer}>
                        <div className={styles.tabSlider} style={{ transform: `translateX(-${activeTabIndex * 20}%)` }}>
                            <div className={styles.tabSlide}>
                                <div className={styles.formSection}>
                                    {duplicateInfo && (
                                        <>
                                            {duplicateInfo.hasGeometryDuplicate && (
                                                <div className={styles.duplicateNoticeBox}>
                                                    <div className={styles.duplicateNoticeHeader}>
                                                        <AlertTriangleIcon size={16} />
                                                        <span>Дубликат геометрии (совпадений: {duplicateInfo.otherFeatures?.length || 1})</span>
                                                    </div>
                                                    {duplicateInfo.categoryInfo?.label && (
                                                        <p className={styles.duplicateNoticeText}>
                                                            {duplicateInfo.categoryInfo.label}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                            {duplicateInfo.hasCadastralDuplicate && (
                                                <div className={styles.duplicateNoticeBox}>
                                                    <div className={styles.duplicateNoticeHeader}>
                                                        <AlertTriangleIcon size={16} />
                                                        <span>Дубликат кад. номера (совпадений: {duplicateInfo.otherFeatures?.length || 1})</span>
                                                    </div>
                                                    <p className={styles.duplicateNoticeText}>
                                                        Кадастровый номер совпадает с другими объектами
                                                    </p>
                                                </div>
                                            )}
                                            {duplicateInfo.hasProximityDuplicate && !duplicateInfo.hasGeometryDuplicate && (
                                                <div className={styles.duplicateNoticeBox}>
                                                    <div className={styles.duplicateNoticeHeader}>
                                                        <AlertTriangleIcon size={16} />
                                                        <span>Дубликат координат (совпадений: {duplicateInfo.otherFeatures?.length || 1})</span>
                                                    </div>
                                                    <p className={styles.duplicateNoticeText}>
                                                        Точки расположены в одной позиции
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {propsConfig.filter(p => ['name', 'cadastral_number', 'category', 'height', 'area'].includes(p.name)).map(renderProperty)}
                                </div>
                            </div>
                            <div className={styles.tabSlide}>
                                <div className={styles.formSection}>{renderOwnershipSection()}</div>
                            </div>
                            <div className={styles.tabSlide}>
                                <div className={styles.formSection}>
                                    <h4 className={styles.sectionTitle}>Статусы объекта</h4>
                                    <div className={styles.statusList}>
                                        {statusConfig.map(renderStatusProperty)}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.tabSlide}>
                                <div className={styles.formSection}>
                                    {renderPhotosSection()}
                                    {renderLinksSection()}
                                </div>
                            </div>
                            <div className={styles.tabSlide}>
                                <div className={styles.formSection}>{renderExtraSection()}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </InfoCard>
            <NewMarkerModal
                isVisible={isMarkerModalVisible}
                onClose={() => setMarkerModalVisible(false)}
                onSave={handleNewMarkerCategory}
            />
        </div>
    );
};

export default EditingPanel;
